import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppStore } from '../../App';
import { formatDateTime } from '../../utils/datetime';

// 관리자 > 제안 페이지 — static/moboard/*.html (브로셔·영업 제안·기관별 제안) 목록.
// 기관별 제안 페이지는 링크 없는 noindex 페이지라 URL 을 외워야 했다 → 여기서 모아 열고 URL 을 복사한다.
// 목록은 서버가 디렉터리를 읽어 준다(GET /admin/moboard-pages). 새 페이지는 파일만 추가하면 나타난다.

interface MoboardPage {
  name: string;
  path: string;
  label: string;
  title: string | null;
  isPublic: boolean;
  updatedAt: string;
}

const ProposalPages: React.FC = () => {
  const { user } = useAppStore();
  const [pages, setPages] = useState<MoboardPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    fetch('/admin/moboard-pages', { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j?.success) setPages(j.data.pages); else setError(j?.error || '목록을 불러오지 못했습니다.'); })
      .catch(() => setError('목록을 불러오지 못했습니다.'));
  }, []);

  if (!(user?.isAdmin || user?.role === 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  const absolute = (p: string) => `${window.location.origin}${p}`;
  const copy = async (p: string) => {
    try {
      await navigator.clipboard.writeText(absolute(p));
      setCopied(p);
      setTimeout(() => setCopied(c => (c === p ? null : c)), 1500);
    } catch { /* clipboard 미지원 — 무시 */ }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-1">제안 페이지</h1>
      <p className="text-sm text-gray-500 mb-6">
        MOboard 브로셔·영업 제안·기관별 제안 정적 페이지입니다. 비공개 페이지는 어디에도 링크가 없으니 URL 을 복사해 직접 전달하세요.
        새 페이지는 <code className="text-xs bg-gray-100 px-1">static/moboard/&lt;기관명 영문&gt;.html</code> 파일을 추가하면 목록에 나타납니다.
      </p>

      {error && <div className="text-sm text-red-600 mb-4">{error}</div>}
      {pages === null && !error && <div className="text-sm text-gray-500">불러오는 중…</div>}
      {pages && pages.length === 0 && <div className="text-sm text-gray-500">페이지가 없습니다.</div>}

      {pages && pages.length > 0 && (
        <ul className="divide-y divide-gray-200 border border-gray-200 bg-white">
          {pages.map(p => (
            <li key={p.name} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <a href={p.path} target="_blank" rel="noopener noreferrer" className="font-medium hover:text-blue-600 hover:underline">
                    {p.label}
                  </a>
                  <span className={`text-[10px] px-1.5 py-0.5 ${p.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {p.isPublic ? '공개' : '비공개 · URL 직접 전달'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 font-mono truncate">{absolute(p.path)}</div>
                <div className="text-xs text-gray-400 mt-0.5">수정 {formatDateTime(p.updatedAt)}</div>
              </div>
              <div className="flex gap-2 sm:flex-shrink-0">
                <a href={p.path} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1 text-xs bg-blue-500 text-white hover:bg-blue-600 transition-colors">새 탭에서 열기</a>
                <button type="button" onClick={() => copy(p.path)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                  {copied === p.path ? '복사됨' : 'URL 복사'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProposalPages;
